import axios from 'axios'
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from 'constants/api.constant'
import { PERSIST_STORE_NAME } from 'constants/app.constant'
import deepParseJson from 'utils/deepParseJson'
import { apiError } from 'utils/alerts'
import store from 'store'
import { onSignOutSuccess, setToken } from 'store/auth/sessionSlice'
import { setUser, initialState } from 'store/auth/userSlice'
import { attachWltRequest, wltDecryptResponse, wltDecryptError } from './transport/wltAxios'

const { REACT_APP_URL } = process.env

// Refresh 60 seconds before actual expiry
const REFRESH_BUFFER_MS = 60 * 1000

let isRefreshing = false
let pendingRequests = []

const processQueue = (error, token = null) => {
    pendingRequests.forEach(({ resolve, reject }) =>
        error ? reject(error) : resolve(token)
    )
    pendingRequests = []
}

const forceLogout = () => {
    store.dispatch(onSignOutSuccess())
    store.dispatch(setUser(initialState))
    window.location.href = '/login'
}

const BaseService = axios.create({
    timeout: 60000,
    baseURL: REACT_APP_URL,
    // baseURL: 'http://13.204.189.62:8006/',
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
})

BaseService.interceptors.request.use(
    async config => {
        // The refresh call re-enters this interceptor because it now goes through
        // BaseService. Without this bypass, isRefreshing=true causes the refresh to
        // queue itself into pendingRequests → deadlock.
        if (config.__skipAuth) return config

        const isRiskServiceApi = config.url?.includes('/analytics_service/api/v1/')
        if (isRiskServiceApi) return config

        const rawPersistData = localStorage.getItem(PERSIST_STORE_NAME)
        const persistData = deepParseJson(rawPersistData)
        const session = persistData?.auth?.session ?? store.getState().auth?.session

        let accessToken = session?.token
        const refreshToken = session?.refreshToken
        const tokenExpiry = session?.tokenExpiry

        const isExpiringSoon = tokenExpiry && Date.now() >= tokenExpiry - REFRESH_BUFFER_MS

        if (isExpiringSoon && refreshToken) {
            if (isRefreshing) {
                // Queue this request until the ongoing refresh finishes
                return new Promise((resolve, reject) => {
                    pendingRequests.push({ resolve, reject })
                }).then(newToken => {
                    config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${newToken}`
                    return config
                })
            }

            isRefreshing = true
            try {
                // Route the refresh call through this instance so both auth AND WLT
                // interceptors run on it. Setting __skipAuth prevents the auth
                // interceptor from recursing when it inspects tokenExpiry.
                const resp = await BaseService.post(
                    '/user_service/users/refresh/',
                    { refresh: refreshToken },
                    { headers: { 'Content-Type': 'application/json' }, __skipAuth: true }
                )
                const { access, expires_in } = resp.data
                const newExpiry = Date.now() + (expires_in || 1800) * 1000
                store.dispatch(setToken({ access, tokenExpiry: newExpiry }))
                processQueue(null, access)
                accessToken = access
            } catch (err) {
                processQueue(err)
                forceLogout()
                return Promise.reject(err)
            } finally {
                isRefreshing = false
            }
        }

        if (accessToken) {
            config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
        }

        return config
    },
    error => Promise.reject(error)
)

// Attach the WLT1 request interceptor AFTER the auth block. Axios request
// interceptors run LIFO, so WLT runs first — seals the body and adds its
// headers, then the auth interceptor adds `Authorization` on the outside.
attachWltRequest(BaseService)

BaseService.interceptors.response.use(
    async response => {
        try {
            await wltDecryptResponse(response)
        } catch (err) {
            apiError(err)
            return Promise.reject(err)
        }
        return response
    },
    async error => {
        await wltDecryptError(error)
        apiError(error)
        return Promise.reject(error)
    }
)

export default BaseService
